import React, { useState, useEffect } from "react";
import { Table, Spin, Space, Typography, Select } from "antd";
import { LoadingOutlined, UserOutlined, ShopOutlined } from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import dayjs, { Dayjs } from "dayjs";
import { PageLayout, PageHeader, SectionCard, TableToolbar } from "../../Common/Layout";
import AMLReportService, { IAMLListItem } from "../../Services/AMLReportService";
import { IPractices, usePractice } from "../../Context/PracticeContext";

const { Text } = Typography;

const formatDate = (value?: string | null): string => { 
  if (!value) return "-";
  return dayjs(value).format("DD/MM/YYYY");
};

interface AMLListState {
  items: IAMLListItem[];
  totalItems: number;
}

const AMLReports: React.FC = () => {
  const { allPracticeIds } = usePractice();
  const [loading, setLoading] = useState(false);
  const [refresh, setRefresh] = useState(false);
  const [dataList, setDataList] = useState<AMLListState>({ items: [], totalItems: 0 });
  // const [practiceOptions, setPracticeOptions] = useState<{ value: number; label: string }[]>([]);

  const [listParams, setListParams] = useState<{
    practiceId?: number;
    search: string;
    dateRange: [Dayjs | null, Dayjs | null];
    sortDir: string;
    sortCol: string;
    currentPage: number;
    pageSize: number;
  }>({
    practiceId: undefined,
    search: "",
    dateRange: [
      dayjs().startOf("month"),
      dayjs().endOf("month"),
    ],
    sortDir: "descend",
    sortCol: "Date",
    currentPage: 1,
    pageSize: 15,
  });

  useEffect(() => {
    if (allPracticeIds?.length > 0) {
      setListParams((prev) => ({
        ...prev,
        practiceId: prev.practiceId ?? allPracticeIds[0].value,
      }));
    }
  }, [allPracticeIds]);

  const fetchItems = async (start: number, length: number) => {
    if (!listParams.practiceId) return;

    setLoading(true);
    try {
      const response = await AMLReportService.getAMLList(
        listParams.practiceId,
        start,
        length,
        listParams.sortCol,
        listParams.sortDir === "ascend" ? "ascend" : "descend",
        listParams.search,
        listParams.dateRange[0]?.format("DD-MM-YYYY"),
        listParams.dateRange[1]?.format("DD-MM-YYYY"),
      );

      const result = response?.result ?? response;
      setDataList({
        items: Array.isArray(result?.items) ? result.items : [],
        totalItems: result?.totalRecords ?? 0,
      });
    } catch (error) {
      console.error("Failed to load AML list", error);
      setDataList({ items: [], totalItems: 0 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems((listParams.currentPage - 1) * listParams.pageSize, listParams.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listParams, refresh]);

  const handleSearch = (value: string) => {
    setListParams((prev) => ({ ...prev, search: value, currentPage: 1 }));
  };

  const handleRefresh = () => {
    setListParams((prev) => ({
      ...prev,
      search: "",
      dateRange: [
        dayjs().startOf("month"),
        dayjs().endOf("month"),
      ],
      currentPage: 1,
      pageSize: 15,
    }));

    setRefresh((r) => !r);
  };

  const handlePracticeChange = (value?: number) => {
    setListParams((prev) => ({ ...prev, practiceId: value, currentPage: 1 }));
  };

  const filterCriteria = [
    {
      key: "dateRange",
      label: "Date",
      type: "dateRange" as const,
    },
  ];

  const activeFilters = {
    dateRange: listParams.dateRange,
  };

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setListParams((prev) => ({
      ...prev,
      dateRange:
        newFilters.dateRange ??
        [dayjs().startOf("month"), dayjs().endOf("month")],
      currentPage: 1,
    }));
  };

  const columns: ColumnsType<IAMLListItem> = [
    {
      title: "S.No.",
      key: "sno",
      width: "5%",
      render: (_: unknown, __: IAMLListItem, index: number) =>
        listParams.currentPage > 1
          ? listParams.pageSize * (listParams.currentPage - 1) + index + 1
          : index + 1,
    },
    {
      title: "Member Name",
      dataIndex: "memberName",
      key: "MemberName",
      width: "20%",
      sorter: true,
      render: (name: string) => (
        <Space>
          <UserOutlined style={{ color: "#1890ff" }} />
          <Text strong>{name || "N/A"}</Text>
        </Space>
      ),
    },
    {
      title: "Company Name",
      dataIndex: "businessName",
      key: "BusinessName",
      width: "20%",
      sorter: true,
      render: (name: string) => (
        <Space>
          <ShopOutlined style={{ color: "#52c41a" }} />
          <span>{name || "N/A"}</span>
        </Space>
      ),
    },
    {
      title: "Provider",
      dataIndex: "provider",
      key: "Provider",
      width: "12%",
      render: (val: string) => <span>{val || "-"}</span>,
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "Date",
      width: "16%",
      sorter: true,
      render: (val: string) => <span>{formatDate(val)}</span>,
    },
  ];

  return (
    <PageLayout>
      <PageHeader title="AML" subtitle="Anti-money laundering check results for company members" />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            onSearch={handleSearch}
            searchPlaceholder="Search member or company name..."
            filterCriteria={filterCriteria}
            activeFilters={activeFilters}
            onFiltersChange={handleFiltersChange}
            onRefresh={handleRefresh}
            extra={
              <Select
                placeholder="Select Practice"
                style={{ width: 220 }}
                value={listParams.practiceId}
                onChange={handlePracticeChange}
                options={allPracticeIds.map((p: IPractices) => ({
                  value: p.value,
                  label: p.label + " (ID: " + p.value + ")",
                }))}
              />
            }
          />
        </div>
        <Table
          dataSource={dataList.items}
          columns={columns}
          rowKey={"Id"}
          size="small"
          scroll={{ x: 1000, y: window.innerHeight - 270 }}
          loading={{ spinning: loading, indicator: <Spin indicator={<LoadingOutlined />} /> }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            current: listParams.currentPage,
            pageSize: listParams.pageSize,
            showSizeChanger: true,
            total: dataList.totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging, _filter, sorter) => {
            if (paging?.current === undefined || paging?.pageSize === undefined) return;

            const current = paging.current;
            const pageSize = paging.pageSize;
            const sort = Array.isArray(sorter) ? sorter[0] : sorter;

            setListParams((prev) => ({
              ...prev,
              sortDir: sort?.order ? (sort.order as string) : "descend",
              sortCol: sort?.order ? String(sort.columnKey) : "Date",
              currentPage: current,
              pageSize: pageSize,
            }));
          }}
        />
      </SectionCard>
    </PageLayout>
  );
};

export default AMLReports;